import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { LocalizableMessage } from 'components/LocalizableMessage/LocalizableMessage';
import { DateFilterContainer } from './DateFilterContainer';
import { TimeFilterContainer } from './TimeFilterContainer';
import {
  levelFilterDefaultLabel,
  amenityFilterDefaultLabel,
  activityFilterDefaultLabel,
  radiusDefaultLabel,
  creditsLabels,
} from './filters.messages';
import { MultiSelectFilterOptionsContainer } from './MultiSelectFilterOptionsContainer';
import { RESULT_TYPES, FILTER_NAMES, VERTICALS } from '../Search.constants';
import { FilterGroupContainer } from './FilterGroupContainer';
import { RadiusFilterContainer } from './RadiusFilterContainer';
import { RadiusFilterOptionsContainer } from './RadiusFilterOptionsContainer';
import { FilterOptionsWrapper } from '../components/filters/FilterOptionsWrapper';
import { MultiSelectFilterContainer } from './MultiSelectFilterContainer';
import { CreditsFilterContainer } from './CreditsFilterContainer';
import { CreditsFilterOptionsContainer } from './CreditsFilterOptionsContainer';

const activityFilter = (
  <MultiSelectFilterContainer
    filterName={FILTER_NAMES.ACTIVITY}
    defaultLabel={activityFilterDefaultLabel}
    key={FILTER_NAMES.ACTIVITY}
  />
);
const levelFilter = (
  <MultiSelectFilterContainer
    filterName={FILTER_NAMES.LEVEL}
    defaultLabel={levelFilterDefaultLabel}
    key={FILTER_NAMES.LEVEL}
  />
);
const amenityFilter = (
  <MultiSelectFilterContainer
    filterName={FILTER_NAMES.AMENITY}
    defaultLabel={amenityFilterDefaultLabel}
    key={FILTER_NAMES.AMENITY}
  />
);

const levelPanel = <MultiSelectFilterOptionsContainer filterName={FILTER_NAMES.LEVEL} />;
const amenityPanel = <MultiSelectFilterOptionsContainer filterName={FILTER_NAMES.AMENITY} />;

const filterItems = [
  {
    filter: activityFilter,
    shouldDisplay: settings => settings.vertical !== VERTICALS.gym,
  },
  {
    filter: <DateFilterContainer key={FILTER_NAMES.DATE} />,
    shouldDisplay: settings => settings.resultType === RESULT_TYPES.SCHEDULE && !settings.isMobile,
  },
  {
    filter: <TimeFilterContainer key={FILTER_NAMES.TIME} />,
    shouldDisplay: settings => settings.resultType === RESULT_TYPES.SCHEDULE,
  },
];

// TODO - Update all filters to follow pattern of using separate container for option selector
//        all filter items should follow this pattern for more flexibility
const extraFilterItems = [
  {
    name: FILTER_NAMES.CREDITS,
    filter: <CreditsFilterContainer key={FILTER_NAMES.CREDITS} />,
    optionContainer: (
      <FilterOptionsWrapper
        label={<LocalizableMessage message={creditsLabels.default} />}
        panelNode={<CreditsFilterOptionsContainer />}
        key={FILTER_NAMES.CREDITS}
      />
    ),
    shouldDisplay: settings => settings.resultType === RESULT_TYPES.SCHEDULE,
  },
  {
    name: FILTER_NAMES.LEVEL,
    filter: levelFilter,
    optionContainer: (
      <FilterOptionsWrapper label={levelFilterDefaultLabel} panelNode={levelPanel} key={FILTER_NAMES.LEVEL} />
    ),
    shouldDisplay: settings =>
      settings.resultType === RESULT_TYPES.SCHEDULE && ![VERTICALS.wellness, VERTICALS.gym].includes(settings.vertical),
  },
  {
    name: FILTER_NAMES.AMENITY,
    filter: amenityFilter,
    optionContainer: (
      <FilterOptionsWrapper label={amenityFilterDefaultLabel} panelNode={amenityPanel} key={FILTER_NAMES.AMENITY} />
    ),
    shouldDisplay: () => true,
  },
  {
    name: FILTER_NAMES.RADIUS,
    filter: <RadiusFilterContainer key={FILTER_NAMES.RADIUS} />,
    optionContainer: (
      <FilterOptionsWrapper
        label={radiusDefaultLabel}
        panelNode={<RadiusFilterOptionsContainer excludeFromWriteActions isResetEditAfterChange={false} />}
        key={FILTER_NAMES.RADIUS}
      />
    ),
    shouldDisplay: settings => !settings.hideRadiusFilter,
  },
];

const getExtraFilterOptionWrappers = filterSettings =>
  extraFilterItems
    .filter(filterItem => filterItem.shouldDisplay(filterSettings))
    .map(filterItem => ({
      name: filterItem.name,
      container: filterItem.optionContainer,
    }));

export const SecondaryFilters = props => {
  const { vertical, resultType, isMobile, hideRadiusFilter } = props;

  const shouldGroupExtraFilters = !isMobile && resultType === RESULT_TYPES.SCHEDULE;

  const filterSettings = {
    vertical,
    resultType,
    isMobile,
    hideRadiusFilter,
  };

  const allFilterItems = !shouldGroupExtraFilters ? filterItems.concat(extraFilterItems) : filterItems;

  return (
    !!vertical && (
      <Fragment>
        {allFilterItems
          .filter(filterItem => filterItem.shouldDisplay(filterSettings))
          .map(filterItem => filterItem.filter)}
        {shouldGroupExtraFilters && (
          <FilterGroupContainer filterOptionContainers={getExtraFilterOptionWrappers(filterSettings)} />
        )}
      </Fragment>
    )
  );
};

SecondaryFilters.propTypes = {
  resultType: PropTypes.string,
  isMobile: PropTypes.bool,
  hideRadiusFilter: PropTypes.bool,
};
